import { PageProps, useAppRootContext } from '@jusbrasil-web/shared-app-root-provider'
import Head from 'next/head'
import Script from 'next/script'
import { useEffect } from 'react'

import { TOPBAR_INTERNAL_DATA } from './constants'
import styles from './topbar.module.scss'
import { TopbarData, WithTopbarProps } from './types'

export interface TopbarProps {
  // TODO: remove pageProps when AppRootProvider replaces it completely. @felipemsantana
  pageProps: {
    [TOPBAR_INTERNAL_DATA]?: TopbarData | null
  }
}
const lazyCssLinkOnLoadHandler = "if (media !== 'all') media = 'all'".replace(/'/g, '&apos;')

export function Topbar({ pageProps }: TopbarProps) {
  const { getPagePropRequired } = useAppRootContext<WithTopbarProps<PageProps>>()
  const data = getPagePropRequired(TOPBAR_INTERNAL_DATA) || pageProps[TOPBAR_INTERNAL_DATA]

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && data === null) {
      throw new Error(
        'Topbar was disabled in this page. Missing correct "withTopbarProps" usage in getServerSideProps'
      )
    }
  }, [data])

  if (!data || data.disabled) {
    return null
  }

  const lazyCssLink = `
    <link
      rel="stylesheet"
      href="${data.css}"
      media="not all"
      onload="${lazyCssLinkOnLoadHandler}"
    >
  `
    .replace(/\s+/g, ' ')
    .trim()

  return (
    <>
      <Head>
        <link rel="preload" as="script" href={data.js} />
        <link rel="preload" as="style" href={data.css} />
        {data.criticalCss && <style className="critical-css">{data.criticalCss}</style>}
      </Head>

      <Script
        id="lazy-css"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `document.getElementById('lazy-css').insertAdjacentHTML('afterend', '${lazyCssLink}');`,
        }}
      />
      <noscript>
        <link rel="stylesheet" href={data.css} />
      </noscript>

      <Script
        id="remove-critical-css"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            (function () {
              var elems = document.querySelectorAll('.critical-css');
              Array.prototype.forEach.call(elems, function (e) {
                e.parentElement.removeChild(e);
              });
            }());
          `,
        }}
      />

      <div className={styles['Topbar-container']} dangerouslySetInnerHTML={{ __html: data.html }} />

      <script
        dangerouslySetInnerHTML={{
          __html: `window.topbarInitialState = ${JSON.stringify(data.topbarInitialState)} || {};`,
        }}
      />
      <Script src={data.js} strategy="lazyOnload" />
    </>
  )
}
